import React, { useMemo } from 'react';
import Head from 'next/head';
import { useFormat } from 'helpers/hooks/useFormat';
import { getAppropriateSkuName } from 'helpers/productHelpers';
import { StringHelpers } from 'helpers/stringHelpers';
import { IMetaDataComponentProps } from './types/IMetaDataComponentProps';

const MetaDataComponent = ({ product, content, path, tasticName }: IMetaDataComponentProps) => {
  const { formatMessage } = useFormat({ name: 'product' });

  // Memoize default meta values to prevent recreation on every render
  const defaultMeta = useMemo(
    () => ({
      title: formatMessage({
        id: 'meta.title',
        defaultMessage: 'Cameras and photography equipment from Nikon',
      }),
      description: formatMessage({
        id: 'meta.desc',
        defaultMessage: 'Find high quality cameras and photography accessories at Nikon',
      }),
    }),
    [formatMessage],
  );

  // Memoize product-specific meta data computation
  const productMeta = useMemo(() => {
    if (!product) return null;

    const selectedSku = path?.split('/').pop();
    const isBuyNow = selectedSku ? StringHelpers.isNumeric(selectedSku) : false;
    const selectedVariant = isBuyNow ? product.variants.find((v) => v.sku === selectedSku) : product.variants[0];

    const skuName = selectedVariant
      ? getAppropriateSkuName(product.name || '', selectedVariant as any)
      : (StringHelpers.validate(product.name) ?? StringHelpers.validate(defaultMeta.title));

    const title = formatMessage({
      id: 'overview.meta.title',
      defaultMessage: 'Nikon {product} | {category} | Nikon USA',
      values: {
        product: skuName || '',
        category: product.categories?.length ? product.categories[product.categories.length - 1].name || '' : '',
      },
    });

    return {
      title,
      description:
        StringHelpers.validate(product.metaDescription) ??
        StringHelpers.validate(product.description) ??
        StringHelpers.validate(defaultMeta.description),
      keywords: Array.isArray(product.searchKeywords)
        ? product.searchKeywords.join(', ')
        : (product.searchKeywords ?? ''),
      image: selectedVariant?.representativeImage ?? '',
    };
  }, [product, path, formatMessage, defaultMeta]);

  // Memoize content-specific meta data computation
  const contentMeta = useMemo(() => {
    if (!content) return null;

    const kws = content.meta_keywords ?? content.meta_keyword;
    return {
      title: StringHelpers.validate(content.meta_title) ?? defaultMeta.title,
      description: StringHelpers.validate(content.meta_description) ?? StringHelpers.validate(defaultMeta.description),
      keywords: Array.isArray(kws) ? kws.join(', ') : (kws ?? ''),
      image: typeof content.meta_image === 'string' ? content.meta_image : (content.meta_image?.url ?? ''),
    };
  }, [content, defaultMeta]);

  // Select the appropriate meta data source
  const meta = productMeta ??
    contentMeta ?? {
      title: defaultMeta.title,
      description: defaultMeta.description,
      keywords: '',
      image: '',
    };

  // Development logging - only log when necessary and in development
  if (process.env.NODE_ENV === 'development' && tasticName) {
    console.log('[Page Tastic Name]::', tasticName);
    console.log('[Page Tastic data]::', product, content);
  }

  return (
    <Head>
      <title>{meta.title}</title>
      <meta className="swiftype" name="description" data-type="string" content={meta.description} />
      {meta.keywords && <meta className="swiftype" name="keywords" data-type="string" content={meta.keywords} />}
      {meta.image && <meta className="swiftype" name="image" data-type="enum" content={meta.image} />}
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      {path && <meta property="og:url" content={path} />}
      {meta.image && <meta property="og:image" content={meta.image} />}
      <meta property="og:type" content="product" />
    </Head>
  );
};

export default MetaDataComponent;
